/**
 * Dealing in Dollar container (C20, B4 — D01 §5): binds `DollarVault` (packages/ui, DollarVaultView.tsx) to the read
 * model (`GET /dollar`, `GET /dollar/operations`) and posts through the gateway (`useGateway`: preview → PIN → commit,
 * D-018, D-232). Every figure is the server's (ADR-08 §2.1 item 2): the equivalent and the expected profit come from a
 * debounced dry run (`derived.total_iqd`, `derived.profit_iqd`), the sale cost is pre-filled with `costPrefillQuote`
 * (D-239: weighted average of the dollars on hand, as the old screen did — App.tsx:3165-3217), the rate field with the
 * approved buy / sell quote (D-012, D-126). The PIN dialog says why the PIN is asked: a loss sale (old message,
 * App.tsx:18320-18324), a changed cost (D-065), or the operation itself. Only display arithmetic happens here: the
 * whole-IQD remainder of the customer breakdown (BigInt), mirroring the old field sync (DollarVaultView.tsx:878-907);
 * what is posted is the engine's split. Customer mode posts on an existing customer account or opens one inside the
 * operation after the same-name check (C22: D-017, D-018, `newAccounts`); an on-account part beyond the account's
 * balance opens the old excess dialogs (C22, D-218; the old D01 screen had none and let the balance go negative, F-247).
 * Direct deposit / withdrawal (D-211) for holders of `usd_direct_deposit_withdraw`.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DollarVault, RateModal, formatWithCommas,
  type DollarCounterparty, type DollarCustomerOption, type DollarFeedRow, type DollarForm, type DollarOpType, type DollarPreview, type DollarRowKind, type DollarSubTab,
} from "@faylasuf/ui";
import { PRINT_ROOT_CLASS, StatementA4, printDocument } from "@faylasuf/print";
import type { DollarOperationRow, DollarOperationsQuery, PreviewResult } from "../api/types.js";
import { useApp, useSession } from "../app/context.js";
import { engineErrors, errorMessage, isSessionLost } from "../app/errors.js";
import { customerPinMessage, surplusPrimary, useExcessFlow, useNewCustomerName, type CustomerTarget, type ExcessResolution } from "../app/customer-picker.js";
import { useGateway } from "../app/operations.js";
import { keys } from "../app/query.js";
import { useToast } from "../app/toast.js";
import { useAccounts } from "./WalletsPage.js";
import { officeDate } from "./wallets/StatementContainer.js";
import { usePrintOffice } from "../app/printOffice.js";
import { usePrintSettings } from "../app/documents.js";

const EMPTY: DollarForm = {
  opType: "buy",
  directType: "deposit",
  counterparty: "box",
  customerName: "",
  customerId: "",
  newCustomerName: "",
  allocation: "debt",
  receivedCash: "",
  remainingOnAccount: "",
  dueDate: "",
  amount: "",
  rate: "",
  costRate: "",
  note: "",
  trustClientName: "",
};

const positive = (s: string): boolean => /^\d*\.?\d+$/.test(s) && /[1-9]/.test(s);
const abs = (s: string): string => s.replace(/^-/, "");
/** Whole-IQD difference for the breakdown sync (display only; strings of digits). */
function minusWhole(a: string, b: string): string {
  const w = (s: string) => (/^\d+$/.test(s) ? BigInt(s) : 0n);
  const r = w(a) - w(b);
  return (r < 0n ? 0n : r).toString();
}

function rowKind(r: DollarOperationRow): DollarRowKind {
  if (r.type === "usd_buy") return "buy";
  if (r.type === "usd_sell") return "sell";
  if (r.type === "usd_direct_withdraw") return "withdraw";
  if (r.type === "usd_direct_deposit") return "deposit";
  return r.domain === "D05" ? "cash" : "settle";
}

export function DollarPage() {
  const { t, source, locale, now } = useApp();
  const { session, hasPermission, sessionLost } = useSession();
  const printOffice = usePrintOffice(); // [C29] F-286: identity lines in the A4 header
  const printSettings = usePrintSettings(); // [C36 review] D-337: the dollar statement hides profit like the currency statement
  const toast = useToast();
  const gateway = useGateway();
  const qc = useQueryClient();
  const tz = session.office.timeZone;

  const [subTab, setSubTab] = useState<DollarSubTab>("trade");
  const [form, setForm] = useState<DollarForm>(EMPTY);
  const [typedRemaining, setTypedRemaining] = useState(false);
  const [preview, setPreview] = useState<PreviewResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [rateModal, setRateModal] = useState<{ buy: string; sell: string; busy: boolean; error: string | null } | null>(null);
  const [printRows, setPrintRows] = useState<readonly DollarOperationRow[] | null>(null);
  const seq = useRef(0);

  const summaryQuery = useQuery({ queryKey: keys.dollar, queryFn: () => source.dollar() });
  const feedQ: DollarOperationsQuery = { ...(startDate ? { from: startDate } : {}), ...(endDate ? { to: endDate } : {}), ...(debouncedSearch ? { q: debouncedSearch } : {}), limit: 500 };
  const feedQuery = useQuery({ queryKey: keys.dollarOperations(feedQ), queryFn: () => source.dollarOperations(feedQ) });
  const accountsQuery = useAccounts(hasPermission("debts") || hasPermission("view_debts_and_trusts"));
  for (const e of [summaryQuery.error, feedQuery.error]) if (e && isSessionLost(e)) sessionLost();
  const accounts = accountsQuery.data ?? [];
  const names = useNewCustomerName(accounts);
  /** [C22] the new / same-name-resolved customer of the open form (reset when the customer fields change). */
  const target = useRef<CustomerTarget | null>(null);

  useEffect(() => {
    const h = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(h);
  }, [search]);

  const summary = summaryQuery.data;
  const rates = summary?.rates ?? null;
  const prefill = summary?.costPrefillQuote ?? null;

  // The rate field starts with the approved buy quote (D-012 / D-126; the old screen's constant 1,500 was the per-$1 bug).
  useEffect(() => {
    if (rates && form.rate === "" && form.opType === "buy") setForm((f) => ({ ...f, rate: rates.buy }));
  }, [rates?.buy]); // only when the rates arrive

  const patch = (p: Partial<DollarForm>) => {
    if ("customerName" in p || "customerId" in p || "newCustomerName" in p || "allocation" in p || "counterparty" in p) target.current = null;
    setForm((f) => ({ ...f, ...p }));
  };

  const onOpTypeChange = (op: DollarOpType) => {
    setForm((f) => ({
      ...f,
      opType: op,
      ...(op === "buy" ? { rate: rates?.buy ?? f.rate, trustClientName: "" } : {}),
      ...(op === "sell" ? { rate: rates?.sell ?? f.rate, costRate: prefill ?? "" } : {}),
      ...(op === "direct" ? { costRate: "", trustClientName: "" } : {}),
    }));
  };
  const onCounterpartyChange = (cp: DollarCounterparty) => patch(cp === "box" ? { counterparty: cp, dueDate: "" } : { counterparty: cp });

  // ---- the operation the form describes (null = incomplete) ----
  const vaultIds = summary?.vault && summary.iqdVaultId ? { iqdVaultAccountId: summary.iqdVaultId, usdVaultAccountId: summary.vault.id } : null;
  const opType = form.opType === "direct" ? (form.directType === "deposit" ? "usd_direct_deposit" : "usd_direct_withdraw") : form.opType === "buy" ? "usd_buy" : "usd_sell";
  const counterparty = (accountId: string, surplus?: ExcessResolution["surplus"]): Record<string, unknown> =>
    form.counterparty === "box"
      ? { kind: "cash" }
      : {
          kind: "customer",
          accountId,
          ...(surplus ? { surplus } : {}),
          ...(form.receivedCash !== "" ? { cashReceived: form.receivedCash } : typedRemaining && form.remainingOnAccount !== "" ? { remainingOnAccount: form.remainingOnAccount } : {}),
          ...(form.dueDate ? { dueDate: form.dueDate } : {}),
        };
  const addNewLabel = t("dollar.add_new_account");
  /** The name typed for a customer that is not picked from the list (old: created silently, D03 §4.5; now D-017 / D-018). */
  const typedCustomerName = form.customerName === addNewLabel ? form.newCustomerName.trim() : form.customerName.trim();
  const operationInput = (accountId: string = form.customerId, surplus?: ExcessResolution["surplus"]): Record<string, unknown> | null => {
    if (!summary?.vault || !positive(form.amount)) return null;
    if (form.opType === "direct") {
      if (form.directType === "deposit" && !positive(form.costRate)) return null;
      return { usd: form.amount, ...(form.directType === "deposit" ? { costQuote: form.costRate } : {}), reason: form.note.trim(), usdVaultAccountId: summary.vault.id };
    }
    if (!vaultIds || !positive(form.rate)) return null;
    if (form.counterparty === "customer" && !accountId) return null;
    const note = form.note ? { note: form.note } : {};
    if (form.opType === "buy") return { usd: form.amount, quote: form.rate, counterparty: counterparty(accountId, surplus), ...vaultIds, ...note };
    return { usd: form.amount, saleQuote: form.rate, ...(form.costRate ? { costQuote: form.costRate } : {}), counterparty: counterparty(accountId, surplus), ...vaultIds, ...note };
  };
  // The dry run of the preview box: an unpicked customer is planned as cash (total and profit do not depend on it).
  const unpicked = form.counterparty === "customer" && !form.customerId && typedCustomerName !== "";
  const baseInput = operationInput(unpicked ? "unpicked" : form.customerId);
  const input = baseInput && unpicked ? { ...baseInput, counterparty: { kind: "cash" } } : baseInput;
  const inputKey = JSON.stringify([opType, input]);

  // Debounced dry run: the preview box shows only what the server planned.
  useEffect(() => {
    setPreview(null);
    if (!input || form.opType === "direct") return;
    const my = ++seq.current;
    const h = setTimeout(async () => {
      try {
        const pv = await gateway.preview(opType, input);
        if (my === seq.current) setPreview(pv);
      } catch (e) {
        if (my === seq.current && isSessionLost(e)) sessionLost();
        // other refusals surface on submit (old: toast after the click)
      }
    }, 300);
    return () => clearTimeout(h);
  }, [inputKey]); // the preview depends on the operation only

  const total = preview?.derived["total_iqd"];
  const profit = preview?.derived["profit_iqd"];
  const previewBox: DollarPreview | null = total
    ? {
        totalLabel: formatWithCommas(total),
        ...(form.opType === "sell" && profit !== undefined
          ? { profit: { label: profit.startsWith("-") ? `-${formatWithCommas(abs(profit))}` : profit === "0" ? "0" : `+${formatWithCommas(profit)}`, tone: profit.startsWith("-") ? ("negative" as const) : profit === "0" ? ("zero" as const) : ("positive" as const) } }
          : {}),
      }
    : null;

  // Old field sync (DollarVaultView.tsx:144-164, 878-907) over the server total.
  useEffect(() => {
    if (form.counterparty !== "customer") return;
    if (!total) return setForm((f) => (f.receivedCash === "" && f.remainingOnAccount === "" ? f : { ...f, receivedCash: "", remainingOnAccount: "" }));
    if (form.receivedCash === "" && !typedRemaining) setForm((f) => ({ ...f, remainingOnAccount: total }));
  }, [total, form.counterparty]);
  const onCashReceivedChange = (v: string) => {
    setTypedRemaining(false);
    patch({ receivedCash: v, remainingOnAccount: total ? minusWhole(total, v) : "" });
  };
  const onRemainingChange = (v: string) => {
    setTypedRemaining(true);
    patch({ remainingOnAccount: v, receivedCash: total ? minusWhole(total, v) : "" });
  };

  // ---- customers (existing accounts; a new one is opened inside the operation, C22) ----
  const customerOptions: DollarCustomerOption[] = accounts
    .filter((a) => a.kind === (form.allocation === "debt" ? "customer_debt" : "customer_trust") && a.currency === "IQD" && !a.archived)
    .map((a) => ({ id: a.id, name: a.name ?? "", amountLabel: `${formatWithCommas(a.balance)} ${t("dollar.iqd")} (${form.allocation === "debt" ? t("dollar.debts") : t("dollar.trusts")})` }));
  const onCustomerPick = (o: DollarCustomerOption | "new") => {
    if (o === "new") patch({ customerName: addNewLabel, customerId: "" });
    else patch({ customerName: o.name, customerId: o.id });
  };

  const excess = useExcessFlow({
    accounts,
    // old option 1 (CurrencyVaultView.tsx:608-630): the on-account part becomes the trust balance, the rest cash
    onReduce: (balance) => {
      onRemainingChange(balance);
      toast(t("excess.reduced_cv"), "info");
    },
    onResolved: (res) => void submit(res),
  });

  const canSubmit =
    input !== null &&
    (form.counterparty !== "customer" || form.customerId !== "" || typedCustomerName !== "") &&
    (form.opType !== "sell" || form.costRate !== "") &&
    (form.opType !== "direct" || form.note.trim() !== "");

  const submit = async (excessRes?: ExcessResolution) => {
    if (!input) return;
    // C22: a customer typed but not picked → same-name check, then the account is opened inside the operation.
    if (form.counterparty === "customer" && !form.customerId && !target.current) {
      const resolved = await names.resolve(typedCustomerName, form.allocation, "IQD", "c");
      if (!resolved) return;
      target.current = resolved;
    }
    const accountId = form.counterparty === "customer" ? form.customerId || target.current?.accountId || "" : "";
    const opInput = operationInput(accountId, excessRes?.surplus);
    if (!opInput) return;
    const newAccounts = [...(form.counterparty === "customer" && !form.customerId ? (target.current?.newAccounts ?? []) : []), ...(excessRes?.newAccounts ?? [])];
    setBusy(true);
    let pv: PreviewResult;
    try {
      pv = await gateway.previewWithNew(opType, opInput, newAccounts);
    } catch (e) {
      if (isSessionLost(e)) sessionLost();
      else {
        const sur = engineErrors(e).find((x) => x.code === "SURPLUS_ACCOUNT_REQUIRED");
        const primary = sur ? surplusPrimary(accounts, String(sur.params["account"] ?? ""), target.current, form.allocation, "IQD") : undefined;
        if (sur && primary && !excessRes) excess.open({ account: primary, surplus: String(sur.params["surplus"] ?? "0") });
        else toast(errorMessage(t, e), "error");
      }
      setBusy(false);
      return;
    }
    try {
      const usd = formatWithCommas(form.amount);
      let message: string;
      if (opType === "usd_sell" && (pv.derived["profit_iqd"] ?? "").startsWith("-")) message = t("pin.message_usd_loss", { loss: formatWithCommas(abs(pv.derived["profit_iqd"]!)) });
      else if (opType === "usd_sell" && pv.derived["prefilled_cost_quote"] !== form.costRate) message = t("pin.message_usd_cost_override", { prefilled: pv.derived["prefilled_cost_quote"] || "-", cost: form.costRate });
      else if (opType === "usd_buy" || opType === "usd_sell") message = t(opType === "usd_buy" ? "pin.message_usd_buy" : "pin.message_usd_sell", { usd, rate: form.rate, total: formatWithCommas(pv.derived["total_iqd"] ?? "0") });
      else message = t(`pin.message_${opType}`, { usd });
      const opened = [...(newAccounts.length > 0 && target.current?.newAccount && !form.customerId ? [target.current.newAccount] : []), ...(excessRes?.newAccount ? [excessRes.newAccount] : [])];
      message = customerPinMessage(t, message, { newAccounts: opened, ...(excessRes ? { excess: { amount: pv.derived["surplus_iqd"] ?? "0", currency: t("dollar.iqd"), target: excessRes.targetLabel } } : {}) });
      const res = await gateway.commitPreview(pv, { pinMessage: message });
      if (res) {
        target.current = null;
        // Old step 5 (D01 §2): fields cleared, rates kept.
        setForm((f) => ({ ...EMPTY, opType: f.opType, directType: f.directType, counterparty: f.counterparty, allocation: f.allocation, rate: f.rate, costRate: f.opType === "sell" ? f.costRate : "" }));
        setTypedRemaining(false);
        void qc.invalidateQueries({ queryKey: keys.dollar });
      }
    } catch {
      /* toasted by the gateway */
    } finally {
      setBusy(false);
    }
  };

  // After a commit the summary's pre-fill may have moved: refresh the cost of an open sale form.
  useEffect(() => {
    if (form.opType === "sell" && prefill !== null && form.costRate === "") setForm((f) => ({ ...f, costRate: prefill }));
  }, [prefill]);

  // ---- feed ----
  const fmtDateTime = useMemo(() => new Intl.DateTimeFormat(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz, dateStyle: "short", timeStyle: "medium" }), [locale, tz]);
  const fmtTime = useMemo(() => new Intl.DateTimeFormat(locale === "ar" ? "ar-IQ" : "en-US", { timeZone: tz, hour: "2-digit", minute: "2-digit" }), [locale, tz]);
  const ops = feedQuery.data?.rows ?? [];
  const feedRows: DollarFeedRow[] = ops.map((r) => {
    const title = t(`optype.${r.type}`);
    return {
      id: r.operationId,
      kind: rowKind(r),
      title: title === `optype.${r.type}` ? r.type : title,
      dateTimeLabel: fmtDateTime.format(new Date(r.postedAt)),
      timeLabel: fmtTime.format(new Date(r.postedAt)),
      note: r.note,
      usdLabel: formatWithCommas(abs(r.usd)),
      iqdLabel: r.iqd ? formatWithCommas(r.iqd) : null,
      profitLabel: r.profitIqd === "0" ? null : r.profitIqd.startsWith("-") ? `-${formatWithCommas(abs(r.profitIqd))}` : `+${formatWithCommas(r.profitIqd)}`,
    };
  });
  const targetRows = (): readonly DollarOperationRow[] => (selectedIds.length > 0 ? ops.filter((r) => selectedIds.includes(r.operationId)) : ops);
  const today = officeDate(now(), tz);

  // WhatsApp share (DollarVaultView.tsx:397-449, D-063: a link opened on the user's device).
  const share = () => {
    const rows = targetRows();
    if (rows.length === 0) return toast(t("dollar.nothing_to_share"), "info");
    const sel = feedQuery.data?.totals;
    const lines = [
      t("dollar.share_header"),
      t("dollar.share_date", { date: today }),
      ...(startDate || endDate ? [t("dollar.share_period", { from: startDate || t("dollar.period_start"), to: endDate || t("dollar.period_today") })] : []),
      "",
      t("dollar.share_summary"),
      // Totals are the server's over the filter when nothing is selected; for a selection, the selected rows' own figures.
      t("dollar.share_total_buy", { v: formatWithCommas(selectedIds.length ? sumRows(rows, "usd_buy") : (sel?.buyUsd ?? "0")) }),
      t("dollar.share_total_sell", { v: formatWithCommas(selectedIds.length ? sumRows(rows, "usd_sell") : (sel?.sellUsd ?? "0")) }),
      t("dollar.share_total_profit", { v: formatWithCommas(selectedIds.length ? sumProfit(rows) : (sel?.profitIqd ?? "0")) }),
      t("dollar.share_count", { n: rows.length }),
      "",
      t("dollar.share_details"),
      ...rows.slice(0, 25).map((r, i) => {
        const k = rowKind(r);
        const line = t("dollar.share_line", { i: i + 1, type: t(`dollar.share_type_${k}`), usd: formatWithCommas(abs(r.usd)), rate: r.rate ? formatWithCommas(r.rate) : "-", date: r.officeDate });
        return r.note ? `${line} - _${r.note}_` : line;
      }),
      ...(rows.length > 25 ? [t("dollar.share_more", { n: rows.length - 25 })] : []),
      "",
      t("dollar.share_footer"),
    ];
    window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(lines.join("\n"))}`, "_blank", "noopener");
    toast(t("dollar.whatsapp_opened"), "success");
  };

  // p05: the USD operations report (App.tsx:18411-18520) on the A4 template; PDF = the same print until the B5 worker.
  const print = () => {
    const rows = targetRows();
    if (rows.length === 0) return toast(t("dollar.nothing_to_save"), "info");
    setPrintRows(rows);
  };
  useEffect(() => {
    if (!printRows) return;
    const done = () => setPrintRows(null);
    window.addEventListener("afterprint", done, { once: true });
    const h = setTimeout(() => printDocument("professional-print-template"), 80);
    return () => {
      clearTimeout(h);
      window.removeEventListener("afterprint", done);
    };
  }, [printRows]);

  // ---- trusts (USD customer trusts; "Sell this" pre-fills a cash sale, the trust is untouched — App.tsx:46343, F-239) ----
  const trustRows = (summary?.trusts.rows ?? []).map((r) => ({ id: r.id, name: r.name ?? "", usdLabel: formatWithCommas(r.balance) }));
  const sellTrust = (id: string) => {
    const r = summary?.trusts.rows.find((x) => x.id === id);
    if (!r) return;
    setForm((f) => ({ ...f, opType: "sell", counterparty: "box", amount: r.balance, rate: rates?.sell ?? f.rate, costRate: prefill ?? "", trustClientName: r.name ?? "", note: t("dollar.sell_trust_note", { name: r.name ?? "" }) }));
    setSubTab("trade");
    toast(t("dollar.sell_trust_selected", { name: r.name ?? "", amount: formatWithCommas(r.balance) }), "info");
  };

  // ---- rate dialog (D-126: settings_currencies + own-or-supervisor PIN) ----
  const saveRates = async () => {
    if (!rateModal) return;
    setRateModal({ ...rateModal, busy: true, error: null });
    try {
      const res = await gateway.pin.withPin((proof) => source.setRate("USD", rateModal.buy, rateModal.sell, proof), { message: t("pin.message_rates"), allowSupervisor: true });
      if (res === undefined) return setRateModal((m) => (m ? { ...m, busy: false } : m));
      setRateModal(null);
      toast(t("toast.rates_updated"), "success");
      void qc.invalidateQueries({ queryKey: keys.rates });
      void qc.invalidateQueries({ queryKey: keys.dollar });
    } catch (e) {
      if (isSessionLost(e)) return sessionLost();
      setRateModal((m) => (m ? { ...m, busy: false, error: errorMessage(t, e) } : m));
    }
  };

  const canViewVault = hasPermission("view_main_vault_usd");
  const cashLabel = formatWithCommas(canViewVault ? (summary?.vault?.total ?? "0") : summary?.cash?.kind === "custody" ? summary.cash.balance : "0");

  return (
    <>
      <DollarVault
        cards={{
          canViewVault,
          username: session.user.username,
          cashLabel,
          profitLabel: formatWithCommas(feedQuery.data?.totals.profitIqd ?? "0"),
          trustsTotalLabel: formatWithCommas(summary?.trusts.totalUsd ?? "0"),
          trustsCount: summary?.trusts.count ?? 0,
          buyRateLabel: formatWithCommas(rates?.buy ?? "0"),
          sellRateLabel: formatWithCommas(rates?.sell ?? "0"),
          canEditRates: hasPermission("settings_currencies"),
        }}
        subTab={subTab}
        onSubTabChange={setSubTab}
        onEditRates={() => setRateModal({ buy: rates?.buy ?? "", sell: rates?.sell ?? "", busy: false, error: null })}
        form={form}
        onFormChange={patch}
        onOpTypeChange={onOpTypeChange}
        onCounterpartyChange={onCounterpartyChange}
        showDirect={hasPermission("usd_direct_deposit_withdraw")}
        ratePlaceholder={form.opType === "sell" ? (rates?.sell ?? "") : (rates?.buy ?? "")}
        costPlaceholder={prefill ?? rates?.buy ?? ""}
        customerOptions={customerOptions}
        onCustomerPick={onCustomerPick}
        onCashReceivedChange={onCashReceivedChange}
        onRemainingChange={onRemainingChange}
        preview={form.opType === "direct" ? null : previewBox}
        canSubmit={canSubmit && !busy}
        busy={busy}
        onSubmit={() => void submit()}
        recent={feedRows.slice(0, 5)}
        feed={feedRows}
        selectedIds={selectedIds}
        onSelectedChange={setSelectedIds}
        onShare={share}
        onPdf={print}
        onPrint={print}
        search={search}
        onSearchChange={setSearch}
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        onPresetToday={() => {
          setStartDate(today);
          setEndDate(today);
        }}
        onPresetMonth={() => {
          setStartDate(`${today.slice(0, 7)}-01`);
          setEndDate(today);
        }}
        trusts={trustRows}
        onSellTrust={sellTrust}
      />
      {excess.dialog}
      {names.dialog}
      <RateModal
        open={rateModal !== null}
        buy={rateModal?.buy ?? ""}
        sell={rateModal?.sell ?? ""}
        onBuyChange={(v) => setRateModal((m) => (m ? { ...m, buy: v } : m))}
        onSellChange={(v) => setRateModal((m) => (m ? { ...m, sell: v } : m))}
        onSubmit={() => void saveRates()}
        onClose={() => setRateModal(null)}
        busy={rateModal?.busy ?? false}
        error={rateModal?.error ?? null}
      />
      {gateway.pin.dialog}
      {printRows &&
        createPortal(
          <div className={PRINT_ROOT_CLASS} data-testid="print-root">
            <StatementA4
              hideProfit={!printSettings.showProfit}
              locale={locale}
              title={t("dollar.print_report_title")}
              customerName={t("dollar.print_division")}
              dateLabel={today}
              docNumber={String(printRows[0]?.chainSeq ?? 0).padStart(6, "0")}
              office={printOffice}
              {...(startDate ? { periodFrom: startDate } : {})}
              {...(endDate ? { periodTo: endDate } : {})}
              columns={[
                { header: t("dollar.print_col_date"), key: "date" },
                { header: t("dollar.print_col_type"), key: "typeLabel" },
                { header: t("dollar.print_col_usd"), key: "usdVal", numeric: true },
                { header: t("dollar.print_col_rate"), key: "rate", numeric: true },
                { header: t("dollar.print_col_iqd"), key: "iqdVal", numeric: true },
                { header: t("dollar.print_col_profit"), key: "profit", numeric: true, profit: true },
                { header: t("dollar.print_col_note"), key: "note" },
              ]}
              rows={printRows.map((r) => ({
                date: fmtDateTime.format(new Date(r.postedAt)),
                typeLabel: t(`dollar.print_type_${rowKind(r)}`),
                usdVal: `$${formatWithCommas(abs(r.usd))}`,
                rate: r.rate ? formatWithCommas(r.rate) : "-",
                iqdVal: r.iqd ? `${formatWithCommas(r.iqd)} ${t("dollar.iqd")}` : "-",
                profit: r.profitIqd !== "0" ? `${formatWithCommas(r.profitIqd)} ${t("dollar.iqd")}` : "-",
                note: r.note || "-",
              }))}
              summary={[
                { label: t("dollar.print_total_buy"), value: `$${formatWithCommas(sumRows(printRows, "usd_buy"))}` },
                { label: t("dollar.print_total_sell"), value: `$${formatWithCommas(sumRows(printRows, "usd_sell"))}` },
                { label: t("dollar.print_total_profit"), value: `${formatWithCommas(sumProfit(printRows))} ${t("dollar.iqd")}`, profit: true },
                { label: t("dollar.print_count"), value: String(printRows.length) },
              ]}
            />
          </div>,
          document.body,
        )}
    </>
  );
}

/**
 * Σ of decimal strings with at most 2 places (USD cents / whole IQD) for the print / share summary of a **selection**
 * (display only, BigInt in hundredths; the unselected totals are the server's). Old: `reduce` over floats.
 */
function sumDecimal(values: readonly string[]): string {
  let cents = 0n;
  for (const v of values) {
    const neg = v.startsWith("-");
    const [i = "0", f = ""] = abs(v).split(".");
    const c = BigInt(i) * 100n + BigInt((f + "00").slice(0, 2));
    cents += neg ? -c : c;
  }
  const neg = cents < 0n;
  const a = neg ? -cents : cents;
  const frac = (a % 100n).toString().padStart(2, "0");
  return `${neg ? "-" : ""}${(a / 100n).toString()}${frac === "00" ? "" : `.${frac.replace(/0$/, "")}`}`;
}
function sumRows(rows: readonly DollarOperationRow[], type: string): string {
  return sumDecimal(rows.filter((r) => r.type === type).map((r) => abs(r.usd)));
}
function sumProfit(rows: readonly DollarOperationRow[]): string {
  return sumDecimal(rows.map((r) => r.profitIqd));
}
